import { cn } from "@babybjorn/ui";
import type { FC, ImgHTMLAttributes } from "react";
import * as s from "../Image.module.scss";
import { useStoryblokImage } from "./useStoryblokImage";

type StoryblokImageProps = {
	src: string;
	className?: string;
	fill?: boolean;
	width?: number;
	height?: number;
	priority?: boolean;
	tolerance?: number;
	quality?: number;
	hidePlaceholder?: boolean;
	aspectRatio?: number;
} & ImgHTMLAttributes<HTMLImageElement>;

const StoryblokImage: FC<StoryblokImageProps> = ({
	className,
	src,
	width,
	height,
	fill = false,
	priority = false,
	tolerance,
	loading = "lazy",
	sizes,
	quality = 80,
	hidePlaceholder = false,
	aspectRatio,
	...rest
}) => {
	const {
		imageWidth,
		imageHeight,
		originalFormat,
		placeholderSrc,
		createSourceUrl,
		formats,
	} = useStoryblokImage({
		src,
		width,
		height,
		quality,
		tolerance,
		aspectRatio,
	});

	const showPlaceholder =
		!hidePlaceholder && originalFormat !== "png" && originalFormat !== "webp";

	return (
		<div
			className={cn(s.image, { [s.fill]: fill })}
			style={{ aspectRatio: `${imageWidth}/${imageHeight}` }}
		>
			<picture>
				{showPlaceholder && (
					<img
						src={placeholderSrc}
						className={s.placeholder}
						aria-hidden="true"
						alt=""
						width={imageWidth}
						height={imageHeight}
						loading={loading}
					/>
				)}
				{formats.map((format) => (
					<source
						key={format}
						type={`image/${format}`}
						srcSet={createSourceUrl(format)}
						sizes={sizes}
					/>
				))}
				<img
					alt={rest.alt}
					loading={priority || loading === "eager" ? "eager" : "lazy"}
					decoding="async"
					sizes={sizes}
					className={cn(s.element, className)}
					width={imageWidth}
					height={imageHeight}
					srcSet={createSourceUrl(originalFormat)}
					src={`${src}/m/${imageWidth}x${imageHeight}/smart/filters:quality(${quality})`}
					{...rest}
				/>
			</picture>
		</div>
	);
};

export default StoryblokImage;
